// /crm/proposals/new — cadastro de proposta (Story 11.6)
// Reusa ProposalForm com pickers de produto SaaS e plano. Quando vem de um deal
// (?dealId=), pré-preenche saas/plano/valor/vínculo a partir do deal (AC3/AC10).

import { ArrowLeft } from 'lucide-react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ProposalForm, type ProposalFormValues } from '../../../components/crm/ProposalForm';
import { useCreateCrmProposal } from '../../../hooks/use-crm-proposals';
import { useCrmDeal } from '../../../hooks/use-crm-deals';
import { useCrmPermissions } from '../../../hooks/use-crm-permissions';
import { useSaasProducts } from '../../../hooks/use-saas-products';
import { usePlans } from '../../../hooks/use-plans';
import { extractErrorMessage } from '../../../lib/errors';

export function NewCrmProposalPage(): JSX.Element {
  const navigate = useNavigate();
  const { canWrite } = useCrmPermissions();
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get('dealId') ?? undefined;

  const create = useCreateCrmProposal();
  const { data: saasProducts } = useSaasProducts({ limit: 100 });
  const { data: plans } = usePlans({ limit: 100 });
  const { data: deal, isLoading: isLoadingDeal } = useCrmDeal(dealId);

  // Pré-preenche a partir do deal (AC3). Backend também faz o prefill — aqui é
  // só pra UX (campos já vêm preenchidos no form). Aguarda o deal carregar.
  const initialValues: Partial<ProposalFormValues> | undefined = deal
    ? {
        deal_id: deal.id,
        lead_id: deal.lead_id,
        client_id: deal.client_id,
        saas_product_id: deal.saas_product_id ?? '',
        plan_id: deal.plan_id ?? '',
        monthly_value: deal.value !== null ? Number(deal.value) : null,
      }
    : undefined;

  // Quando há ?dealId mas o deal ainda está carregando, segura o form pra não
  // renderizar com defaults vazios e depois "pular" (evita flash de UI errada).
  const waitingForDeal = Boolean(dealId) && isLoadingDeal;

  // RBAC (FR-59.2 / AC3): read-only roles não acessam o fluxo de criação.
  if (!canWrite) {
    return <Navigate to="/crm/proposals" replace />;
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 pb-10">
      <header className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => navigate('/crm/proposals')}
          className="inline-flex w-fit items-center gap-1 text-[12px] text-muted transition-colors hover:text-ink"
        >
          <ArrowLeft size={13} strokeWidth={2} />
          Propostas
        </button>
        <h1 className="m-0 text-[26px] font-bold leading-tight tracking-[-0.025em] text-ink">
          Nova proposta
        </h1>
        <p className="text-[13px] text-muted">
          A proposta nasce em rascunho com um número sequencial (PROP-{new Date().getFullYear()}-…).
          {deal ? ' Pré-preenchemos com os dados do deal — ajuste o que precisar.' : ''}
        </p>
      </header>

      {waitingForDeal ? (
        <FormSkeleton />
      ) : (
        <ProposalForm
          key={deal?.id ?? 'blank'}
          initialValues={initialValues}
          isPending={create.isPending}
          submitLabel="Criar rascunho"
          saasProducts={saasProducts?.items ?? []}
          plans={plans?.items ?? []}
          linkedDealTitle={deal?.title ?? null}
          linkedLeadName={deal?.lead?.name ?? null}
          linkedClientName={deal?.client?.name ?? null}
          onCancel={() => navigate('/crm/proposals')}
          onSubmit={async (values) => {
            try {
              const proposal = await create.mutateAsync(values);
              toast.success('Proposta criada.', { description: proposal.proposal_number });
              navigate('/crm/proposals');
            } catch (err) {
              toast.error('Não conseguimos criar a proposta.', {
                description: extractErrorMessage(err, 'Tente de novo em instantes.'),
              });
            }
          }}
        />
      )}
    </div>
  );
}

function FormSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-[14px] border border-line bg-panel p-[20px]">
          <div className="mb-3 h-3 w-32 animate-pulse rounded bg-panel-2" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-10 animate-pulse rounded bg-panel-2" />
            <div className="h-10 animate-pulse rounded bg-panel-2" />
          </div>
        </div>
      ))}
    </div>
  );
}
